import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/features/auth';
import { LoadingSpinner } from '@/shared/components';
import { Play, CheckCircle, Lock, Video, Award } from 'lucide-react';
import VideoPlayer from '../components/VideoPlayer';
import { trainingVideoService, type VideoWithProgress, type VideosCompletionStatus } from '../services/training-video.service';

export default function OnboardingStep5() {
    const [loading, setLoading] = useState(true);
    const [videoStatus, setVideoStatus] = useState<VideosCompletionStatus | null>(null);
    const [selectedVideo, setSelectedVideo] = useState<VideoWithProgress | null>(null);
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const { user, refreshUser } = useAuth();

    useEffect(() => {
        loadVideos();
    }, []);

    const loadVideos = async () => {
        try {
            setLoading(true);
            const response = await trainingVideoService.getMyVideos();
            if (response.success) {
                setVideoStatus(response.data);
                // Auto-select first incomplete video
                const firstIncomplete = response.data.videos.find(v => !v.progress?.completed);
                if (firstIncomplete) {
                    setSelectedVideo(firstIncomplete);
                }
            }
        } catch (err: any) {
            console.error('Error loading videos:', err);
            setError(err.response?.data?.error || 'Failed to load training videos');
        } finally {
            setLoading(false);
        }
    };

    const handleProgressUpdate = async (videoId: string, watchedSeconds: number, completed: boolean) => {
        try {
            await trainingVideoService.updateProgress(videoId, watchedSeconds, completed);
            // Reload videos to get updated progress
            await loadVideos();

            // If video was completed, refresh user data
            if (completed) {
                await refreshUser();
            }
        } catch (err: any) {
            console.error('Error updating progress:', err);
        }
    };

    const handleManualComplete = async (videoId: string, duration: number) => {
        try {
            // Mark video as complete with full duration watched
            await trainingVideoService.updateProgress(videoId, duration, true);
            // Reload videos WITHOUT showing full-screen loader
            await loadVideosQuietly();
            // Refresh user data to check if all videos are complete
            await refreshUser();
        } catch (err: any) {
            console.error('Error marking video complete:', err);
            setError('Failed to mark video as complete');
        }
    };

    const loadVideosQuietly = async () => {
        try {
            // Load videos without setting loading state (no full-screen loader)
            const response = await trainingVideoService.getMyVideos();
            if (response.success) {
                setVideoStatus(response.data);
                // Auto-select first incomplete video
                const firstIncomplete = response.data.videos.find(v => !v.progress?.completed);
                if (firstIncomplete) {
                    setSelectedVideo(firstIncomplete);
                }
            }
        } catch (err: any) {
            console.error('Error loading videos:', err);
            setError(err.response?.data?.error || 'Failed to load training videos');
        }
    };

    const handleContinue = async () => {
        await refreshUser();
        navigate('/onboarding/step-6');
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-teal-50">
                <LoadingSpinner size="lg" />
            </div>
        );
    }

    if (!videoStatus) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-teal-50">
                <div className="bg-white rounded-xl shadow-lg p-8 max-w-md">
                    <p className="text-red-600">{error || 'Failed to load videos'}</p>
                </div>
            </div>
        );
    }

    const trainingVideos = videoStatus.videos.filter(v => v.type === 'TRAINING');
    const complianceVideos = videoStatus.videos.filter(v => v.type === 'COMPLIANCE');

    return (
        <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
                {/* Progress Indicator */}
                <div className="mb-8">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">Step 5 of 8</span>
                        <span className="text-sm font-medium text-emerald-600">Training Videos</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                            className="bg-emerald-600 h-2 rounded-full transition-all duration-500"
                            style={{ width: '62.5%' }}
                        ></div>
                    </div>
                </div>

                {/* Header */}
                <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
                    <div className="flex items-start justify-between">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900 mb-2">
                                Watch Training Videos
                            </h1>
                            <p className="text-gray-600">
                                Complete all required training and compliance videos to continue
                            </p>
                        </div>
                        <div className="text-right">
                            <div className="text-3xl font-bold text-emerald-600">
                                {videoStatus.completedRequired}/{videoStatus.totalRequired}
                            </div>
                            <div className="text-sm text-gray-600">Required Complete</div>
                        </div>
                    </div>

                    {/* Overall Progress Bar */}
                    <div className="mt-4">
                        <div className="flex justify-between text-sm text-gray-600 mb-1">
                            <span>Overall Progress</span>
                            <span>{videoStatus.overallProgress}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                            <div
                                className="bg-emerald-500 h-3 rounded-full transition-all"
                                style={{ width: `${videoStatus.overallProgress}%` }}
                            />
                        </div>
                    </div>

                    {videoStatus.allRequiredComplete && (
                        <div className="mt-4 p-4 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center space-x-3">
                            <Award className="w-6 h-6 text-emerald-600" />
                            <div>
                                <p className="font-medium text-emerald-900">All Required Videos Completed!</p>
                                <p className="text-sm text-emerald-700">You can now continue to the next step</p>
                            </div>
                        </div>
                    )}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Video Player Section */}
                    <div className="lg:col-span-2">
                        {selectedVideo ? (
                            <div className="bg-white rounded-xl shadow-lg p-6">
                                <div className="flex items-start justify-between mb-4">
                                    <div>
                                        <div className="flex items-center space-x-2 mb-2">
                                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                                                selectedVideo.type === 'COMPLIANCE'
                                                    ? 'bg-red-100 text-red-800'
                                                    : 'bg-blue-100 text-blue-800'
                                            }`}>
                                                {selectedVideo.type}
                                            </span>
                                            {selectedVideo.isRequired && (
                                                <span className="px-2 py-1 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                                                    Required
                                                </span>
                                            )}
                                        </div>
                                        <h2 className="text-2xl font-bold text-gray-900">{selectedVideo.title}</h2>
                                        {selectedVideo.description && (
                                            <p className="text-gray-600 mt-1">{selectedVideo.description}</p>
                                        )}
                                    </div>
                                    {selectedVideo.progress?.completed && (
                                        <CheckCircle className="w-8 h-8 text-emerald-500" />
                                    )}
                                </div>

                                <VideoPlayer
                                    videoUrl={selectedVideo.videoUrl}
                                    videoId={selectedVideo.id}
                                    duration={selectedVideo.duration}
                                    initialProgress={selectedVideo.progress?.watchedSeconds || 0}
                                    isCompleted={selectedVideo.progress?.completed || false}
                                    allowSkipping={selectedVideo.allowSkipping}
                                    minWatchPercentage={selectedVideo.minWatchPercentage}
                                    onProgressUpdate={(seconds, completed) =>
                                        handleProgressUpdate(selectedVideo.id, seconds, completed)
                                    }
                                    onManualComplete={() =>
                                        handleManualComplete(selectedVideo.id, selectedVideo.duration)
                                    }
                                />

                                <div className="mt-4 text-sm text-gray-600">
                                    <p>Duration: {Math.floor(selectedVideo.duration / 60)} minutes</p>
                                    <p>Watch Requirement: {selectedVideo.minWatchPercentage}% of video</p>
                                    {!selectedVideo.allowSkipping && (
                                        <p className="text-yellow-600 font-medium">⚠️ Skipping ahead is disabled</p>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="bg-white rounded-xl shadow-lg p-12 text-center">
                                <Video className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                                <p className="text-gray-600">Select a video from the list to begin watching</p>
                            </div>
                        )}
                    </div>

                    {/* Video List Section */}
                    <div className="space-y-6">
                        {/* Training Videos */}
                        {trainingVideos.length > 0 && (
                            <div className="bg-white rounded-xl shadow-lg p-6">
                                <h3 className="font-bold text-lg text-gray-900 mb-4">Training Videos</h3>
                                <div className="space-y-3">
                                    {trainingVideos.map((video) => (
                                        <button
                                            key={video.id}
                                            onClick={() => setSelectedVideo(video)}
                                            className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                                                selectedVideo?.id === video.id
                                                    ? 'border-emerald-500 bg-emerald-50'
                                                    : 'border-gray-200 hover:border-gray-300'
                                            }`}
                                        >
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1">
                                                    <div className="flex items-center space-x-2 mb-1">
                                                        {video.progress?.completed ? (
                                                            <CheckCircle className="w-5 h-5 text-emerald-500" />
                                                        ) : (
                                                            <Play className="w-5 h-5 text-gray-400" />
                                                        )}
                                                        <span className="font-medium text-gray-900 text-sm">
                                                            {video.title}
                                                        </span>
                                                    </div>
                                                    <div className="text-xs text-gray-600 ml-7">
                                                        {Math.floor(video.duration / 60)} min
                                                        {video.isRequired && ' • Required'}
                                                    </div>
                                                    {video.progress && !video.progress.completed && (
                                                        <div className="mt-2 ml-7">
                                                            <div className="w-full bg-gray-200 rounded-full h-1.5">
                                                                <div
                                                                    className="bg-emerald-500 h-1.5 rounded-full"
                                                                    style={{ width: `${video.watchPercentage}%` }}
                                                                />
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Compliance Videos */}
                        {complianceVideos.length > 0 && (
                            <div className="bg-white rounded-xl shadow-lg p-6">
                                <h3 className="font-bold text-lg text-gray-900 mb-4">Compliance Videos</h3>
                                <div className="space-y-3">
                                    {complianceVideos.map((video) => (
                                        <button
                                            key={video.id}
                                            onClick={() => setSelectedVideo(video)}
                                            className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                                                selectedVideo?.id === video.id
                                                    ? 'border-red-500 bg-red-50'
                                                    : 'border-gray-200 hover:border-gray-300'
                                            }`}
                                        >
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1">
                                                    <div className="flex items-center space-x-2 mb-1">
                                                        {video.progress?.completed ? (
                                                            <CheckCircle className="w-5 h-5 text-emerald-500" />
                                                        ) : (
                                                            <Lock className="w-5 h-5 text-red-500" />
                                                        )}
                                                        <span className="font-medium text-gray-900 text-sm">
                                                            {video.title}
                                                        </span>
                                                    </div>
                                                    <div className="text-xs text-gray-600 ml-7">
                                                        {Math.floor(video.duration / 60)} min • Required
                                                    </div>
                                                    {video.progress && !video.progress.completed && (
                                                        <div className="mt-2 ml-7">
                                                            <div className="w-full bg-gray-200 rounded-full h-1.5">
                                                                <div
                                                                    className="bg-red-500 h-1.5 rounded-full"
                                                                    style={{ width: `${video.watchPercentage}%` }}
                                                                />
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Continue Button */}
                        {videoStatus.allRequiredComplete && (
                            <button
                                onClick={handleContinue}
                                className="w-full px-6 py-3 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors"
                            >
                                Continue to Next Step →
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
